
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { ApplyToAllButtonWithConfirm } from "../components/ApplyToAllButtonWithConfirm";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { FieldVendorHint } from "@/components/common/FieldVendorHint";

export const DescriptionSection = ({
  form,
  onApplyLocationDescriptionToAll,
  onApplyDescTimingAndBookingToAll,
  applyToAllLoading = false,
}: {
  form: any;
  onApplyLocationDescriptionToAll?: (description: string) => Promise<void>;
  onApplyDescTimingAndBookingToAll?: (value: string) => Promise<void>;
  applyToAllLoading?: boolean;
}) => {
  const isDescriptionDisabled = !onApplyLocationDescriptionToAll || applyToAllLoading;
  const isTimingDisabled = !onApplyDescTimingAndBookingToAll || applyToAllLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Description</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="descLocation"
          render={({ field }: any) => (
            <FormItem>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FormLabel className="text-left block text-lg">
                    <strong>Location description</strong>
                  </FormLabel>
                  <FieldOwnershipBadge fieldPath="/descLocation" />
                </div>
                {onApplyLocationDescriptionToAll && (
                  <ApplyToAllButtonWithConfirm
                    disabled={isDescriptionDisabled}
                    dialogTitle="Apply to all locations?"
                    dialogDescription="This will overwrite the Location description for every location that shares the same camp organization. This updates drafts and cannot be undone."
                    onConfirm={async () => {
                      const description =
                        typeof form?.getValues === "function" ? (form.getValues("descLocation") ?? "") : "";
                      await onApplyLocationDescriptionToAll(String(description ?? ""));
                    }}
                  />
                )}
              </div>
              <FormControl>
                <Textarea {...field} className="min-h-[200px]" maxLength={2000} />
              </FormControl>
              <FieldVendorHint fieldPath="/descLocation" onUseVendorValue={(v) => form.setValue("descLocation", v as string)} />
              <FormDescription className="text-left">
                An overview that gives a flavor of your camp's focus and philosophy. This is the first thing families will encounter on your page, and they'll want to get an idea of what your camp is like and why kids would want to go there. Feel free to describe typical activities, your facilities, and your general approach, but no need to go into specific programming details such as ages, dates, or timing, as all that info is found elsewhere on the page. If your camp has multiple locations, remember that this description should be specific to just this location. Max. 2,000 characters. {field.value?.length || 0}/2000
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="descTimingAndBooking"
          render={({ field }: any) => (
            <FormItem>
              <div className="flex items-center justify-between gap-3">
                <FormLabel className="text-left block pt-2 text-lg pt-4">
                  <strong>General session and booking information</strong>
                </FormLabel>
                {onApplyDescTimingAndBookingToAll && (
                  <ApplyToAllButtonWithConfirm
                    disabled={isTimingDisabled}
                    dialogTitle="Apply to all locations?"
                    dialogDescription="This will overwrite General session and booking information for every location that shares the same camp organization. This updates drafts and cannot be undone."
                    onConfirm={async () => {
                      const value =
                        typeof form?.getValues === "function" ? (form.getValues("descTimingAndBooking") ?? "") : "";
                      await onApplyDescTimingAndBookingToAll(String(value ?? ""));
                    }}
                  />
                )}
              </div>
              <FormControl>
                <Textarea {...field} className="min-h-[150px]" maxLength={1000} />
              </FormControl>
              <FormDescription className="text-left">
                A brief description of the types of sessions you typically offer. It is always shown and appears above your sessions, so it should be general and not specific to any particular session. It's helpful to mention the times of day and times of year you operate, and whether camp is booked by the day, week, or other duration. Max. 1,000 characters. {field.value?.length || 0}/1000
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};
